import { Box, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { AssignmentCard } from './AssignmentCard';
import { DashboardCard } from './DashboardCard';
import { useAnnotationCardsSection } from './useAnnotationCardsSection';
import { useNavOnDashboard } from './useNavOnDashboard';

export const AnnotationCardsSection = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const {
    pendingTasksCount,
    draftTasksCount,
    finalizedTasksCount,
    isAssignmentCountLoading,
    myProjectsCount,
    isProjectsCountLoading,
    hasSupervisionBatches,
    isHasSupervisionBatchesLoading,
    hasAnnotationTasks,
    isHasAnnotationTasksLoading,
    showBatchReviewCard,
    batchReviewCount,
    isBatchReviewCountLoading,
    isAdmin,
    isUser,
    isManagerWithoutSupervision,
  } = useAnnotationCardsSection();

  const { navigateToAnnotatorQueue, navigateToAnnotationSupervisor, navigateToProjects } =
    useNavOnDashboard();

  const dataLoading =
    isAssignmentCountLoading ||
    isBatchReviewCountLoading ||
    isProjectsCountLoading ||
    isHasAnnotationTasksLoading ||
    isHasSupervisionBatchesLoading;

  // A user with neither role has nothing to work on at all
  const hasNoRole = !isUser && !isAdmin;

  // My Projects (Supervisor Projects) is shown alongside actual supervision batches, or for a
  // manager without supervision batches who does have annotation tasks — but not for a manager
  // with no active work at all.
  const showSupervisorProjects =
    hasSupervisionBatches || (isManagerWithoutSupervision && hasAnnotationTasks);
  const showMyProjectsCard = isUser && (!isManagerWithoutSupervision || hasAnnotationTasks);

  if (hasNoRole) {
    if (dataLoading) {
      return null;
    }
    return (
      <Box sx={{ mt: theme.spacing(6) }}>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          {formatMessage({ id: 'annotationDashboard.annotation.noTaskAssigned' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: theme.spacing(6) }}>
      {!dataLoading && (
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h5">
            {formatMessage({ id: 'annotationDashboard.mywork.cardsSection.title' })}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', width: '100%', gap: theme.spacing(6) }}>
        {/* My Projects card — shown for any ANNOTATION_USER, count may be 0. Not shown for a
            manager retaining Supervisor Workspace access with no active work at all (no
            supervision batches and no annotation tasks). Routes to Supervisor Projects for
            actual supervisors and for a manager without supervision batches who has annotation
            tasks; otherwise routes to Annotator Projects. */}
        {showMyProjectsCard && (
          <DashboardCard
            title={formatMessage({ id: 'annotationDashboard.annotation.myProjects.card.title' })}
            description={formatMessage({
              id: 'annotationDashboard.annotation.myProjects.card.description',
            })}
            value={myProjectsCount}
            isLoading={isProjectsCountLoading}
            onClickHandler={(e) => navigateToProjects(e, showSupervisorProjects)}
          />
        )}

        {/* Batch review / Review conflicts card — shown for supervisor and admin */}
        {showBatchReviewCard && (
          <DashboardCard
            title={formatMessage({ id: 'annotationDashboard.annotation.batchReview.card.title' })}
            description={formatMessage({
              id: 'annotationDashboard.annotation.batchReview.card.description',
            })}
            value={batchReviewCount}
            borderColor={theme.palette.primary.light}
            onClickHandler={navigateToAnnotationSupervisor}
            isLoading={isBatchReviewCountLoading}
          />
        )}

        {/* Current assignments card — shown for any ANNOTATION_USER, count may be 0 */}
        {isUser && (
          <AssignmentCard
            title={formatMessage({
              id: 'annotationDashboard.annotation.currentAssignments.card.title',
            })}
            pendingCount={pendingTasksCount}
            draftCount={draftTasksCount}
            completedCount={finalizedTasksCount}
            onClickHandler={navigateToAnnotatorQueue}
            isLoading={isAssignmentCountLoading}
          />
        )}
      </Box>
    </Box>
  );
};
